import { Component, inject } from '@angular/core';
import { Router, RouterLink, RouterLinkActive } from '@angular/router';
import { MatListModule } from '@angular/material/list';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { CommonModule } from '@angular/common';
import { AuthService } from '../../../core/services/auth.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-sidebar',
  standalone: true,
  imports: [
    CommonModule,
    RouterLink,
    RouterLinkActive,
    MatListModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    TranslateModule
  ],
  template: `
    <div class="sidebar-container">
      <!-- Header with Logo -->
      <div class="sidebar-header">
        <div class="logo-text">
          <div class="app-title">{{ 'app.title' | translate }}</div>
          <div class="app-subtitle">System</div>
        </div>
      </div>

      <!-- Navigation Links -->
      <mat-nav-list class="nav-section">
        <a mat-list-item routerLink="/" routerLinkActive="active" [routerLinkActiveOptions]="{ exact: true }">
          <mat-icon matListItemIcon>dashboard</mat-icon>
          <span>{{ 'nav.dashboard' | translate }}</span>
        </a>

        <!-- Dobavljači Section -->
        <div class="nav-section-label">Dobavljači</div>
        <a mat-list-item routerLink="/suppliers" routerLinkActive="active">
          <mat-icon matListItemIcon>local_shipping</mat-icon>
          <span>Pregled dobavljača</span>
        </a>
        <ng-container *ngIf="auth.isAdmin">
          <a mat-list-item routerLink="/payables" routerLinkActive="active">
            <mat-icon matListItemIcon>receipt_long</mat-icon>
            <span>Dugovanja</span>
          </a>
          <a mat-list-item routerLink="/payments" routerLinkActive="active">
            <mat-icon matListItemIcon>payments</mat-icon>
            <span>Uplate</span>
          </a>
          <a mat-list-item routerLink="/import" routerLinkActive="active">
            <mat-icon matListItemIcon>upload_file</mat-icon>
            <span>Import iz Excel-a</span>
          </a>
        </ng-container>

        <!-- Klijenti Section -->
        <ng-container *ngIf="auth.isAdmin">
          <div class="nav-section-label">Klijenti</div>
          <a mat-list-item routerLink="/clients" routerLinkActive="active">
            <mat-icon matListItemIcon>people</mat-icon>
            <span>{{ 'nav.clients' | translate }}</span>
          </a>
        </ng-container>

        <!-- Promet Section -->
        <div class="nav-section-label">Promet</div>
        <a mat-list-item routerLink="/turnover" routerLinkActive="active">
          <mat-icon matListItemIcon>trending_up</mat-icon>
          <span>Stanje prometa</span>
        </a>

        <!-- Topli obroci Subsection -->
        <div class="nav-subsection-label">Topli obroci</div>
        <ng-container *ngIf="auth.isAdmin">
          <a mat-list-item routerLink="/prices" routerLinkActive="active" class="subsection-item">
            <mat-icon matListItemIcon>attach_money</mat-icon>
            <span>{{ 'nav.prices' | translate }}</span>
          </a>
          <a mat-list-item routerLink="/daily-entry" routerLinkActive="active" class="subsection-item">
            <mat-icon matListItemIcon>restaurant</mat-icon>
            <span>{{ 'nav.dailyEntry' | translate }}</span>
          </a>
        </ng-container>
        <a mat-list-item routerLink="/reports" routerLinkActive="active" class="subsection-item">
          <mat-icon matListItemIcon>description</mat-icon>
          <span>{{ 'nav.reports' | translate }}</span>
        </a>
        <a mat-list-item routerLink="/control" routerLinkActive="active" class="subsection-item">
          <mat-icon matListItemIcon>playlist_add_check</mat-icon>
          <span>{{ 'nav.control' | translate }}</span>
        </a>

        <!-- Admin Section -->
        <ng-container *ngIf="auth.isAdmin">
          <div class="nav-section-label">Administracija</div>
          <a mat-list-item routerLink="/users" routerLinkActive="active">
            <mat-icon matListItemIcon>group</mat-icon>
            <span>{{ 'nav.users' | translate }}</span>
          </a>
        </ng-container>
      </mat-nav-list>

      <!-- User Info and Logout at Bottom -->
      <div class="sidebar-footer">
        <div class="user-info" *ngIf="auth.currentUser$ | async as user">
          <mat-icon class="user-avatar">account_circle</mat-icon>
          <div class="user-details">
            <div class="user-email">{{ user.email }}</div>
            <div class="user-role">{{ (user.roles && user.roles.length > 0) ? user.roles[0] : 'User' }}</div>
          </div>
        </div>
        <button mat-button class="logout-button" (click)="logout()">
          <mat-icon>logout</mat-icon>
          <span>{{ 'auth.logout' | translate }}</span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .sidebar-container {
      display: flex;
      flex-direction: column;
      height: 100%;
      background: #1F2937;
      color: #FFFFFF;
    }

    .sidebar-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 24px 20px;
      border-bottom: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.2);
    }

    .logo-section {
      display: flex;
      align-items: center;
      gap: 14px;
      flex: 1;
    }

    .logo-icon {
      width: 40px;
      height: 40px;
      font-size: 40px;
      color: #3B82F6;
      filter: drop-shadow(0 0 8px rgba(59, 130, 246, 0.4));
    }

    .logo-text {
      display: flex;
      flex-direction: column;
    }

    .app-title {
      font-size: 19px;
      font-weight: 700;
      line-height: 1.2;
      color: #FFFFFF;
      letter-spacing: -0.02em;
    }

    .app-subtitle {
      font-size: 11px;
      color: #9CA3AF;
      line-height: 1;
      font-weight: 500;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .lang-button {
      color: #9CA3AF;
      transition: all 0.2s ease;
    }

    .lang-button:hover {
      color: #3B82F6;
      background: rgba(59, 130, 246, 0.1);
      transform: scale(1.05);
    }

    .nav-section {
      flex: 1;
      overflow-y: auto;
      overflow-x: hidden; /* Prevent horizontal scrollbar */
      padding: 16px 0;
    }

    .nav-section-label {
      font-size: 11px;
      font-weight: 600;
      color: #6B7280;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 16px 24px 8px 24px;
      margin-top: 8px;
    }

    .nav-section-label:first-of-type {
      margin-top: 0;
    }

    .nav-subsection-label {
      font-size: 10px;
      font-weight: 600;
      color: #4B5563;
      text-transform: uppercase;
      letter-spacing: 0.05em;
      padding: 12px 24px 6px 32px;
      margin-top: 4px;
    }

    .nav-section a[mat-list-item].subsection-item {
      padding-left: 16px;
    }

    .nav-section a[mat-list-item] {
      color: #9CA3AF !important;
      margin: 4px 12px;
      border-radius: 0;
      height: 48px;
      transition: all 0.2s ease;
      font-weight: 500;
      display: flex;
      align-items: center; /* Ensure icon and text are vertically centered */
    }

    .nav-section a[mat-list-item] span {
      color: #9CA3AF !important;
    }

    .nav-section a[mat-list-item]:hover {
      background: #2B374A !important;
      color: #FFFFFF !important;
    }

    .nav-section a[mat-list-item]:hover span {
      color: #FFFFFF !important;
    }

    .nav-section a[mat-list-item].active {
      background: #2B374A !important;
      color: #FFFFFF !important;
      box-shadow: none;
      border-radius: 0;
    }

    .nav-section a[mat-list-item].active span {
      color: #FFFFFF !important;
    }

    .nav-section a[mat-list-item].active mat-icon {
      color: #FFFFFF !important;
    }

    .nav-section a[mat-list-item] mat-icon {
      margin-right: 14px;
      color: #9CA3AF !important;
      transition: all 0.2s ease;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
    }

    /* Align projected MDC list content as well (for safety across MDC internals) */
    .nav-section .mat-mdc-list-item .mdc-list-item__content {
      display: flex;
      align-items: center;
    }

    .nav-section a[mat-list-item]:hover mat-icon {
      color: #FFFFFF !important;
    }

    .sidebar-footer {
      padding: 16px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      background: rgba(0, 0, 0, 0.2);
      margin-top: auto;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 14px 0;
      background: transparent;
      border-radius: 0;
      margin-bottom: 12px;
      border: none;
    }

    .user-avatar {
      width: 44px;
      height: 44px;
      font-size: 44px;
      color: #3B82F6;
    }

    .user-details {
      flex: 1;
      min-width: 0;
    }

    .user-email {
      font-size: 13px;
      font-weight: 600;
      color: #FFFFFF;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .user-role {
      font-size: 11px;
      color: #9CA3AF;
      font-weight: 500;
    }

    .logout-button {
      width: 100%;
      height: 42px;
      border: none;
      color: #1F2937;
      font-weight: 600;
      transition: all 0.2s ease;
      background: #FFFFFF;
      border-radius: 4px;
    }

    .logout-button:hover {
      background: #E5E7EB;
      color: #1F2937;
      transform: translateY(-1px);
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    }

    .logout-button mat-icon {
      margin-right: 8px;
      font-size: 20px;
      width: 20px;
      height: 20px;
    }

    /* Scrollbar styling */
    .nav-section::-webkit-scrollbar {
      width: 6px;
    }

    .nav-section::-webkit-scrollbar-track {
      background: rgba(0, 0, 0, 0.2);
      border-radius: 10px;
    }

    .nav-section::-webkit-scrollbar-thumb {
      background: rgba(59, 130, 246, 0.3);
      border-radius: 10px;
    }

    .nav-section::-webkit-scrollbar-thumb:hover {
      background: rgba(59, 130, 246, 0.5);
    }
  `]
})
export class SidebarComponent {
  auth = inject(AuthService);
  private translate = inject(TranslateService);
  private router = inject(Router);

  useLang(lang: 'en' | 'sr') {
    this.translate.use(lang);
  }

  logout() {
    this.auth.logout();
    this.router.navigateByUrl('/auth');
  }
}
